import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Trophy, Medal, Award, Star, Globe, TrendingUp, 
  Search, ChevronUp, ExternalLink, RefreshCw, Share2
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "general", label: "General" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "blog", label: "Blog" },
  { id: "saas", label: "SaaS" },
  { id: "portfolio", label: "Portfolio" }
];

const getBadgeIcon = (badge) => {
  switch(badge) {
    case "gold": return <Trophy className="w-5 h-5 text-yellow-500" />;
    case "silver": return <Medal className="w-5 h-5 text-gray-400" />;
    case "bronze": return <Award className="w-5 h-5 text-orange-600" />;
    case "top10": return <Star className="w-4 h-4 text-purple-500" />;
    default: return null;
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600 bg-green-50";
  if (score >= 60) return "text-yellow-600 bg-yellow-50";
  if (score >= 40) return "text-orange-600 bg-orange-50";
  return "text-red-600 bg-red-50";
};

export default function LeaderboardPage() {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState("all");
  const [submitUrl, setSubmitUrl] = useState("");
  const [submitName, setSubmitName] = useState("");
  const [submitCategory, setSubmitCategory] = useState("general");

  useEffect(() => {
    document.title = "SEO Leaderboard - Top 100 Websites | MarkeeMix";
    fetchLeaderboard();
    fetchStats();
  }, [category]);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/api/leaderboard/top?limit=100&category=${category}`);
      const data = await res.json();
      if (data.success) {
        setLeaderboard(data.leaderboard);
      }
    } catch (e) {
      console.error("Failed to fetch leaderboard", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/leaderboard/stats`);
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!submitUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leaderboard/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: submitUrl,
          owner_name: submitName || "Anonymous",
          category: submitCategory
        })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Submitted! Rank #${data.current_rank} with score ${data.seo_score}`);
        setSubmitUrl("");
        setSubmitName("");
        fetchLeaderboard();
      } else {
        toast.error(data.detail || "Submission failed");
      }
    } catch (e) {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (websiteId) => {
    try {
      const res = await fetch(`${API}/api/leaderboard/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website_id: websiteId })
      });
      if (res.ok) {
        toast.success("Vote recorded!");
        fetchLeaderboard();
      }
    } catch (e) {
      toast.error("Vote failed");
    }
  };

  const shareLeaderboard = () => {
    if (navigator.share) {
      navigator.share({
        title: "SEO Leaderboard - Top 100 Websites",
        text: "Check out the top websites ranked by SEO score!",
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Trophy className="w-10 h-10 text-yellow-500" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              SEO Leaderboard
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Top 100 websites ranked by SEO score. Submit your site and compete!
          </p>
          
          {/* Stats */}
          {stats && (
            <div className="flex items-center justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-600" />
                <span className="font-bold">{stats.total_websites}</span>
                <span className="text-gray-500">websites</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="font-bold">{stats.average_score}</span>
                <span className="text-gray-500">avg score</span>
              </div>
            </div>
          )}
        </div>

        {/* Submit Form */}
        <Card className="p-6 mb-8">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-600" />
            Submit Your Website
          </h2>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-4 gap-4">
            <Input
              placeholder="https://yourwebsite.com"
              value={submitUrl}
              onChange={(e) => setSubmitUrl(e.target.value)}
              className="md:col-span-2"
            />
            <Input
              placeholder="Your name (optional)"
              value={submitName}
              onChange={(e) => setSubmitName(e.target.value)}
            />
            <select
              value={submitCategory}
              onChange={(e) => setSubmitCategory(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              {CATEGORIES.filter(c => c.id !== "all").map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <Button type="submit" disabled={submitting} className="md:col-span-4 bg-gradient-to-r from-purple-600 to-pink-600">
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              {submitting ? "Analyzing..." : "Submit & Get Ranked"}
            </Button>
          </form>
        </Card>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(c => (
              <Button
                key={c.id}
                variant={category === c.id ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </Button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={shareLeaderboard}>
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Leaderboard */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : leaderboard.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No websites yet. Be the first to submit!
            </div>
          ) : (
            <div className="divide-y">
              {leaderboard.map((site, i) => (
                <div 
                  key={site.website_id}
                  className={`p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                    highlightId === site.website_id ? "bg-purple-50 ring-2 ring-purple-400" : ""
                  } ${site.rank <= 3 ? "bg-gradient-to-r from-yellow-50/50 to-transparent" : ""}`}
                >
                  {/* Rank */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                    site.rank === 1 ? "bg-yellow-400 text-yellow-900" :
                    site.rank === 2 ? "bg-gray-300 text-gray-700" :
                    site.rank === 3 ? "bg-orange-300 text-orange-800" :
                    "bg-gray-100 text-gray-600"
                  }`}>
                    {site.rank}
                  </div>
                  
                  {/* Website Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <a 
                        href={site.url} 
                        target="_blank" 
                        rel="noreferrer"
                        className="font-semibold text-gray-900 hover:text-purple-600 truncate flex items-center gap-1"
                      >
                        {site.domain}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {getBadgeIcon(site.badge)}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>by {site.owner_name}</span>
                      <Badge variant="outline" className="text-xs">{site.category}</Badge>
                    </div>
                  </div>
                  
                  {/* Score */}
                  <div className={`px-4 py-2 rounded-lg font-bold text-xl ${getScoreColor(site.seo_score)}`}>
                    {site.seo_score}
                  </div>
                  
                  {/* Vote */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleVote(site.website_id)}
                    className="flex flex-col items-center"
                  >
                    <ChevronUp className="w-5 h-5" />
                    <span className="text-xs">{site.votes}</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* CTA */}
        <Card className="p-8 mt-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <h2 className="text-2xl font-bold mb-2">Want to Improve Your Score?</h2>
          <p className="opacity-90 mb-4">Get a detailed SEO audit with actionable recommendations</p>
          <Button asChild size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
            <a href="/free/seo-audit">Free SEO Audit</a>
          </Button>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
